<template>
	<div class="new-event-page-view">
		<wait-modal id="new-event-page-wait-modal" ref="waitModal" :title="eventDisplayName || shortName"></wait-modal>

		<section v-if="loadError" class="new-event-page__load-error">
			<p>{{globalErrors.loadFailed}}</p>
			<button type="button" @click="loadEvent">{{globalErrors.tryAgain}}</button>
		</section>

		<main v-else-if="fetchedEvent && accessResolved" class="new-event-page">
		<section class="new-event-page__hero">
			<picture v-if="eventBanner" class="new-event-page__hero-background" aria-hidden="true">
				<source media="(max-width: 600px)" :srcset="eventBanner.mobile">
				<img :src="eventBanner.desktop" alt="">
			</picture>

			<div v-if="fetchedEvent.showEventLogo && eventTranslation.imagePath" class="new-event-page__logo-panel">
				<svg class="new-event-page__logo-vector" width="562" height="251" viewBox="0 0 562 251" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
					<path d="M107.358 196.402C40.6922 179.668 19.6852 196.721 0 206.412V0H561.027C561.345 10.6598 543.95 36.0373 495.233 49.3207C411.251 72.2196 407.995 120.66 400.862 155.008C395.455 181.042 382.329 237.973 310.819 248.365C219.911 261.576 175.982 213.627 107.358 196.402Z" fill="#EFEDED"/>
				</svg>
				<img
					class="new-event-page__event-logo"
					:src="getStorageUrl(eventTranslation.imagePath)"
					:alt="eventTranslation.name"
				>
			</div>
		</section>

		<section class="new-event-page__details">
			<div class="new-event-page__inner new-layout-container">
				<div class="new-event-page__summary">
					<h1>{{eventDisplayName}}</h1>
					<p class="new-event-page__meta">
						<span>{{periodLabel}}</span>
						<span v-if="localName">{{localName}}</span>
						<span v-if="destinationName">{{destinationName}}</span>
					</p>
					<a v-if="fetchedEvent.email" class="new-event-page__email" :href="`mailto:${fetchedEvent.email}`">
						<img src="/static/buttons/new/email.svg" alt="">
						<span>{{fetchedEvent.email}}</span>
					</a>
				</div>

				<event-options
					:options="options"
					:event="fetchedEvent"
					:active-resource="activeOptionResource"
					@select="setActiveOption"
				/>
			</div>
		</section>

		<component
			:is="activeOptionComponent"
			v-if="activeOptionComponent"
			v-bind="activeOptionProps"
			@close="clearActiveOption"
			@select-option="setActiveOption"
		/>

		<official-agency-description
			v-if="fetchedEvent.showOfficialAgencyDescription && !activeOptionComponent"
			:event-name="eventDisplayName"
		/>
		</main>
	</div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import EventService from '../../services/EventService';
import EventOptionService from '../../services/EventOptionService';
import Options, { options as eventOptions } from '../../enums/Options';
import { getStorageUrl } from '../../util';
import { translate } from '../../assets/translations';
import VisaAndVaccine from '../visa-and-vaccine/VisaAndVaccine.vue';
import AboutBrazil from '../destination-info/AboutBrazil.vue';
import DestinationInfo from '../destination-info/DestinationInfo.vue';
import FlightReservation from '../flight-reservation/FlightReservation.vue';
import HotelPrices from '../hotel-reservation/HotelPrices.vue';
import LocalTourPrice from '../local-tour/LocalTourPrice.vue';
import EventOptions from './EventOptions.vue';
import PrePostTours from '../local-tour/PrePostTours.vue';
import TransferPrice from '../transfer-reservation/TransferPrice.vue';
import WoobaFlightReservation from './WoobaFlightReservation.vue';
import OfficialAgencyDescription from './OfficialAgencyDescription.vue';
import WaitModal from '../shared/WaitModal.vue';

const EVENT_BANNERS_BY_DESTINATION_ID = {
	6712713: {
		desktop: '/static/event-banners/rj.png',
		mobile: '/static/event-banners/rj_mobile.png',
	},
	6698758: {
		desktop: '/static/event-banners/campos_jordao.png',
		mobile: '/static/event-banners/campos_jordao_mobile.png',
	},
	189882: {
		desktop: '/static/event-banners/bh.png',
		mobile: '/static/event-banners/bh_mobile.png',
	},
	6375537: {
		desktop: '/static/event-banners/fortaleza.png',
		mobile: '/static/event-banners/fortaleza_mobile.png',
	},
};

export default {
	components: {
		AboutBrazil,
		DestinationInfo,
		FlightReservation,
		HotelPrices,
		LocalTourPrice,
		EventOptions,
		PrePostTours,
		TransferPrice,
		WoobaFlightReservation,
		OfficialAgencyDescription,
		VisaAndVaccine,
		WaitModal,
	},
	props: {
		shortName: {
			type: String,
			required: true,
		},
		event: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			fetchedEvent: this.event,
			loadError: false,
			options: [],
			accessResolved: false,
			activeOptionResource: this.$route.meta.eventOption ? this.$route.meta.eventOption.name : '',
			eventService: new EventService(),
			optionService: new EventOptionService(),
		};
	},
	computed: {
		...mapGetters(['eventHasOption']),
		globalErrors() {
			return translate(this.$store.state.language).globalErrors;
		},
		eventTranslation() {
			if (!this.fetchedEvent || !this.fetchedEvent.translations) return { name: '', imagePath: '' };
			const languageId = this.$store.state.language.id;
			return this.fetchedEvent.translations.find(translation => translation.language.id === languageId)
				|| this.fetchedEvent.translations[0]
				|| { name: '', imagePath: '' };
		},
		eventDisplayName() {
			const name = this.eventTranslation.name || '';
			if (!this.fetchedEvent) return name;
			return this.fetchedEvent.shortName ? `${name} - ${this.fetchedEvent.shortName}` : name;
		},
		periodLabel() {
			if (!this.fetchedEvent.startDate || !this.fetchedEvent.endDate) return '';
			return `${moment(this.fetchedEvent.startDate).format('DD/MM/YYYY')} ${this.pageTranslation.periodConnector} ${moment(this.fetchedEvent.endDate).format('DD/MM/YYYY')}`;
		},
		pageTranslation() {
			return translate(this.$store.state.language).eventPage;
		},
		localName() {
			return this.fetchedEvent.local ? this.fetchedEvent.local.name : '';
		},
		destinationName() {
			return this.fetchedEvent.destination ? this.fetchedEvent.destination.name : '';
		},
		eventBanner() {
			const destinationId = this.fetchedEvent
				&& this.fetchedEvent.destination
				? this.fetchedEvent.destination.id
				: null;
			return EVENT_BANNERS_BY_DESTINATION_ID[destinationId] || null;
		},
		activeOptionComponent() {
			if (this.$route.meta.eventOptionComponent) return this.$route.meta.eventOptionComponent;
			const config = this.activeOptionConfig;
			return config ? config.component : '';
		},
		activeOptionConfig() {
			return eventOptions.find(option => option.name === this.activeOptionResource) || null;
		},
		activeOptionProps() {
			const config = this.activeOptionConfig;
			const props = config && config.passEvent ? { event: this.fetchedEvent } : {};
			if (!this.$route.meta.passFlightReservationParams) return props;

			return Object.assign(props, {
				adults: this.$route.params.adults,
				kids: this.$route.params.kids,
				babies: this.$route.params.babies,
				origin: this.$route.params.origin,
				destination: this.$route.params.destination,
				checkin: this.$route.params.checkin,
				checkout: this.$route.params.checkout,
			});
		},
		routeHasOptionAccess() {
			const eventOption = this.$route.meta.eventOption;
			return !eventOption || this.eventHasOption(eventOption);
		},
	},
	metaInfo() {
		return {
			title: this.eventDisplayName || this.shortName,
		};
	},
	watch: {
		'$route.meta.eventOption'(eventOption) {
			if (eventOption && this.options.length && !this.eventHasOption(eventOption)) {
				this.redirectToEventHome();
				return;
			}
			this.activeOptionResource = eventOption ? eventOption.name : '';
		},
	},
	mounted() {
		this.loadEvent();
	},
	methods: {
		getStorageUrl,
		async loadEvent() {
			this.accessResolved = false;
			this.loadError = false;
			if (this.$refs.waitModal) this.$refs.waitModal.open();

			try {
				try {
					const eventResponse = await this.eventService.getByShortName(this.shortName);
					this.fetchedEvent = this.event ? this.event : eventResponse.data;
				} catch (error) {
					const status = error.response ? error.response.status : null;
					if (status === 404) {
						this.redirectToNotFound();
						return;
					}
					this.loadError = true;
					return;
				}

				try {
					const optionsResponse = await this.optionService.findByEventId(this.fetchedEvent.id);
					this.options = optionsResponse.data;
					this.$store.commit('options', this.options);
					this.$store.commit('event', this.fetchedEvent);
					const hasEventAccess = Boolean(this.fetchedEvent.isOnline);
					const hasOptionAccess = this.routeHasOptionAccess;
					this.accessResolved = hasEventAccess && hasOptionAccess;
					this.handleAccessRedirect(hasEventAccess, hasOptionAccess);
				} catch (error) {
					this.loadError = true;
				}
			} finally {
				if (this.$refs.waitModal) this.$refs.waitModal.close();
			}
		},
		setActiveOption(option) {
			const resource = typeof option === 'string'
				? option
				: option && option.option ? option.option.resource : '';
			const config = eventOptions.find(eventOption => eventOption.name === resource);
			const routeName = config ? resource : '';
			if (!routeName || this.$route.name === routeName) return;
			this.$router.push({
				name: routeName,
				params: {
					shortName: this.shortName,
					event: this.fetchedEvent,
				},
			});
		},
		clearActiveOption() {
			if (this.$route.name === 'event') return;
			this.$router.push({
				name: 'event',
				params: {
					shortName: this.shortName,
					event: this.fetchedEvent,
				},
			});
		},
		redirectToEventHome() {
			this.accessResolved = Boolean(this.fetchedEvent && this.fetchedEvent.isOnline);
			this.$router.replace({
				name: 'event',
				params: {
					shortName: this.shortName,
					event: this.fetchedEvent,
				},
			});
		},
		redirectToNotFound() {
			this.accessResolved = false;
			if (this.$route.name === 'not-found') return;
			this.$router.replace({ name: 'not-found' });
		},
		handleAccessRedirect(hasEventAccess, hasOptionAccess) {
			if (!hasEventAccess) {
				this.$router.replace('/calendar');
				return;
			}
			if (!hasOptionAccess) this.redirectToEventHome();
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-page__load-error {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	min-height: 55vh;
	padding: 50px 5%;
	color: #4c4b4b;
	text-align: center;
}

.new-event-page__load-error p {
	margin: 0 0 20px;
	font-size: 20px;
	line-height: 26px;
}

.new-event-page__load-error button {
	padding: 11px 28px;
	border: 0;
	border-radius: 4px;
	background: #e89127;
	color: #ffffff;
	cursor: pointer;
	font-size: 15px;
	font-weight: 700;
}

.new-event-page__load-error button:hover,
.new-event-page__load-error button:focus-visible {
	background: #cc7815;
}

.new-event-page {
	color: #4c4b4b;
}

.new-event-page__hero {
	position: relative;
	min-height: 357px;
	overflow: hidden;
	background-color: #d8e8ee;
}

.new-event-page__hero-background {
	position: absolute;
	inset: 0;
	display: block;
	width: 100%;
	height: 100%;
}

.new-event-page__hero-background img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.new-event-page__logo-panel {
	position: absolute;
	top: 0;
	left: 0;
	width: 43vw;
	max-width: 562px;
	height: 19.2vw;
	min-height: 190px;
	max-height: 251px;
	overflow: visible;
}

.new-event-page__logo-vector {
	position: absolute;
	inset: 0;
	display: block;
}

.new-event-page__event-logo {
	position: absolute;
	top: 31px;
	left: 145px;
	display: block;
	max-width: 230px;
	max-height: 155px;
	object-fit: contain;
}

.new-event-page__inner {
	padding: 66px 0 72px;
}

.new-event-page__summary {
	margin-bottom: 66px;
}

.new-event-page__summary h1 {
	margin: 0 0 22px;
	color: #002c41;
	font-size: 25px;
	font-weight: 700;
	line-height: 29px;
	text-transform: uppercase;
}

.new-event-page__meta {
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
	margin: 0 0 10px;
	color: #5a5a5a;
	font-size: 20px;
	font-weight: 400;
	line-height: 23px;
}

.new-event-page__email {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #5a5a5a;
	font-size: 20px;
	font-weight: 400;
	line-height: 23px;
	text-decoration: underline;
}

.new-event-page__email:hover {
	color: #11344a;
}

.new-event-page__email img {
	display: block;
	width: 24px;
	height: 18px;
	object-fit: contain;
}

@media (max-width: 900px) {
	.new-event-page__hero {
		min-height: 270px;
		background-position: center top;
	}

	.new-event-page__logo-panel {
		width: 74vw;
		height: 33vw;
		min-height: 140px;
		max-height: 190px;
	}

	.new-event-page__event-logo {
		top: 30px;
		left: 8%;
		max-width: 190px;
		max-height: 120px;
	}

	.new-event-page__inner {
		padding: 42px 0 68px;
	}

	.new-event-page__summary {
		margin-bottom: 42px;
	}

	.new-event-page__summary h1 {
		font-size: 20px;
		line-height: 25px;
	}

	.new-event-page__meta,
	.new-event-page__email {
		font-size: 15px;
		line-height: 21px;
	}
}

@media (max-width: 600px) {
	.new-event-page__hero {
		min-height: 0;
		aspect-ratio: 390 / 454;
	}

	.new-event-page__hero-background img {
		object-fit: fill;
	}

	.new-event-page__logo-panel {
		top: 0;
		left: -0.25%;
		width: 123.6%;
		max-width: none;
		height: 44.5%;
		min-height: 0;
		max-height: none;
	}

	.new-event-page__event-logo {
		top: 6.9%;
		width: 64.36%;
		height: 64.36%;
		max-width: none;
		max-height: none;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/event/Event.vue